import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { usePortalAuth } from '@/contexts/PortalAuthContext';
import PortalNotifications from '@/components/portal/PortalNotifications';
import { Home, FileText, ClipboardList, ListChecks, User, LogOut, CalendarDays } from 'lucide-react';

const NAV = [
  { to: '/portal', label: 'Home', icon: Home, end: true },
  { to: '/portal/schedule', label: 'Schedule', icon: CalendarDays },
  { to: '/portal/documents', label: 'Documents', icon: FileText },
  { to: '/portal/tasks', label: 'Activities', icon: ClipboardList },
  { to: '/portal/checklists', label: 'Checklists', icon: ListChecks },
  { to: '/portal/profile', label: 'Profile', icon: User },
];

// App-like shell for the staff self-service portal: sticky header + a tab row that becomes a
// fixed bottom bar on small screens (thumb-friendly, PWA-installable).
export default function PortalLayout() {
  const { staff, logout } = usePortalAuth();
  const navigate = useNavigate();

  const doLogout = () => {
    logout();
    navigate('/portal/login');
  };

  const tabClass = ({ isActive }) =>
    `flex flex-col sm:flex-row items-center justify-center gap-1 sm:gap-2 flex-1 sm:flex-none px-3 py-2 sm:py-1.5 rounded-xl text-xs sm:text-sm font-medium transition-colors duration-150 ${
      isActive ? 'bg-saffron text-white' : 'text-muted-ink hover:text-charcoal hover:bg-bone'
    }`;

  return (
    <div className="min-h-screen bg-bone flex flex-col">
      <header className="sticky top-0 z-20 bg-white border-b border-border/40">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <img src="/logo-emblem.png" alt="The Tiffin" className="w-9 h-9 rounded-full object-cover" />
            <div className="leading-tight">
              <p className="font-heading text-sm font-700 text-charcoal">Staff Portal</p>
              <p className="text-[11px] text-muted-ink" data-testid="portal-staff-name">{staff?.name} · {staff?.role}</p>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <PortalNotifications />
            <button data-testid="portal-logout-btn" onClick={doLogout} className="flex items-center gap-1.5 text-sm text-muted-ink hover:text-charcoal">
              <LogOut size={16} /> <span className="hidden sm:inline">Log out</span>
            </button>
          </div>
        </div>
        {/* Desktop tabs under header */}
        <nav className="hidden sm:flex max-w-3xl mx-auto px-4 pb-2 gap-2">
          {NAV.map(({ to, label, icon: Icon, end }) => (
            <NavLink key={to} to={to} end={end} data-testid={`portal-tab-${label.toLowerCase()}`} className={tabClass}>
              <Icon size={16} /> {label}
            </NavLink>
          ))}
        </nav>
      </header>

      <main className="flex-1 max-w-3xl w-full mx-auto px-4 py-5 pb-24 sm:pb-8">
        <Outlet />
      </main>

      {/* Mobile bottom tab bar */}
      <nav className="sm:hidden fixed bottom-0 inset-x-0 z-20 bg-white border-t border-border/40 flex px-2 py-1.5">
        {NAV.map(({ to, label, icon: Icon, end }) => (
          <NavLink key={to} to={to} end={end} data-testid={`portal-tab-mobile-${label.toLowerCase()}`} className={tabClass}>
            <Icon size={18} /> {label}
          </NavLink>
        ))}
      </nav>
    </div>
  );
}
